/**
 * Ensures image URLs use HTTPS instead of HTTP to avoid mixed content warnings
 * @param {string} url - The image URL to process
 * @returns {string} The secure image URL or a fallback if the input is invalid
 */
export const getSecureImageUrl = (url) => {
  if (!url) return getFallbackImageUrl();
  
  // Handle protocol-relative URLs (starting with //)
  if (url.startsWith('//')) {
    return `https:${url}`;
  }
  
  // If URL is already HTTPS, return it
  if (url.startsWith('https://')) {
    return url;
  }
  
  // If URL is HTTP, convert to HTTPS
  if (url.startsWith('http://')) {
    return url.replace(/^http:\/\//i, 'https://');
  }
  
  // If URL is relative, assume it's already secure
  if (url.startsWith('/')) {
    return url;
  }
  
  // If URL contains a domain but no protocol, add https://
  if (url.includes('.') && !url.startsWith('data:')) {
    return `https://${url}`;
  }
  
  // For any other format, return as is (might be a data URL or other format)
  return url;
};

/**
 * Provides a fallback URL for when an image fails to load
 * @param {string} itemName - Optional item name to include in the placeholder
 * @returns {string} The fallback image URL
 */
export const getFallbackImageUrl = (itemName = '') => {
  // Using a data URL instead of an external service to avoid DNS resolution issues
  return 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22300%22%20height%3D%22450%22%20viewBox%3D%220%200%20300%20450%22%3E%3Crect%20fill%3D%22%23e0e0e0%22%20width%3D%22300%22%20height%3D%22450%22%2F%3E%3Ctext%20fill%3D%22%23757575%22%20font-family%3D%22Arial%2CHelvetica%2Csans-serif%22%20font-size%3D%2220%22%20text-anchor%3D%22middle%22%20x%3D%22150%22%20y%3D%22225%22%3ENo%20Image%20Available%3C%2Ftext%3E%3C%2Fsvg%3E';
};

/**
 * Gets a console-specific fallback image URL
 * @param {string} consoleName - Name of the console
 * @returns {string} Console-specific fallback image URL
 */
export const getConsoleFallbackImageUrl = (consoleName = '') => {
  // Using a data URL instead of an external service to avoid DNS resolution issues
  return 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22300%22%20height%3D%22450%22%20viewBox%3D%220%200%20300%20450%22%3E%3Crect%20fill%3D%22%23e0e0e0%22%20width%3D%22300%22%20height%3D%22450%22%2F%3E%3Ctext%20fill%3D%22%23757575%22%20font-family%3D%22Arial%2CHelvetica%2Csans-serif%22%20font-size%3D%2220%22%20text-anchor%3D%22middle%22%20x%3D%22150%22%20y%3D%22225%22%3ENo%20Console%20Image%3C%2Ftext%3E%3C%2Fsvg%3E';
};

/**
 * Gets a Lego-specific fallback image URL
 * @param {string} setName - Name of the Lego set
 * @returns {string} Lego-specific fallback image URL
 */
export const getLegoFallbackImageUrl = (setName = '') => {
  return 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22300%22%20height%3D%22450%22%20viewBox%3D%220%200%20300%20450%22%3E%3Crect%20fill%3D%22%23e0e0e0%22%20width%3D%22300%22%20height%3D%22450%22%2F%3E%3Ctext%20fill%3D%22%23757575%22%20font-family%3D%22Arial%2CHelvetica%2Csans-serif%22%20font-size%3D%2220%22%20text-anchor%3D%22middle%22%20x%3D%22150%22%20y%3D%22225%22%3ELego%20Set%20Image%3C%2Ftext%3E%3C%2Fsvg%3E';
};

/**
 * Gets a movie-specific fallback image URL
 * @param {string} movieName - Name of the movie
 * @returns {string} Movie-specific fallback image URL
 */
export const getMovieFallbackImageUrl = (movieName = '') => {
  return 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22300%22%20height%3D%22450%22%20viewBox%3D%220%200%20300%20450%22%3E%3Crect%20fill%3D%22%23e0e0e0%22%20width%3D%22300%22%20height%3D%22450%22%2F%3E%3Ctext%20fill%3D%22%23757575%22%20font-family%3D%22Arial%2CHelvetica%2Csans-serif%22%20font-size%3D%2220%22%20text-anchor%3D%22middle%22%20x%3D%22150%22%20y%3D%22225%22%3EMovie%20Poster%3C%2Ftext%3E%3C%2Fsvg%3E';
};

/**
 * Handles image loading errors by setting a fallback image
 * @param {Event} event - The error event
 * @param {string} itemName - Optional item name for the fallback
 * @param {string} itemType - Optional item type for specific fallbacks
 */
export const handleImageError = (event, itemName = '', itemType = '') => {
  console.warn(`Image failed to load for ${itemName || 'unknown item'}:`, event.target.src);
  
  // Use specific fallback images based on item type
  if (itemType === 'console') {
    event.target.src = getConsoleFallbackImageUrl(itemName);
  } else if (itemType === 'lego') {
    event.target.src = getLegoFallbackImageUrl(itemName);
  } else if (itemType === 'movie') {
    event.target.src = getMovieFallbackImageUrl(itemName);
  } else {
    event.target.src = getFallbackImageUrl(itemName);
  }
  
  // Apply some styling to make the fallback more visible
  event.target.style.backgroundColor = '#f0f0f0';
  event.target.style.padding = '10px';
  
  event.target.onerror = null; // Prevent infinite error loop
};

/**
 * Gets the best available image URL for a console
 * @param {Object} console - Console object
 * @returns {string} Best available image URL
 */
export const getConsoleImageUrl = (console) => {
  if (!console) return getConsoleFallbackImageUrl();
  
  // Try multiple sources in order of preference
  return getSecureImageUrl(
    console.platformLogo || 
    console.imageUrl || 
    getConsoleFallbackImageUrl(console.name)
  );
};
